import { CustomParameters, PaywallResult } from '@accedo/one-plugin-sdk';

import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { noop } from '#/service/utils';
import { authActions } from '#/store/auth/auth.store';

import { StyledSpinner } from '../Splash/Splash.styled';
import { Status } from '../Status/Status';
import { ChangeProductAddPaymentMethod } from './ChangeProductAddPaymentMethod';
import { ChangeProductConfirmation } from './ChangeProductConfirmation';
import { PickProductStep } from './PickProductStep/PickProductStep';
import { usePlanPurchaseFlow } from './PlanPurchaseFlow.hook';
import { Actions, PurchaseFlowStep } from './PlanPurchaseFlow.types';
import { PurchaseProductStep } from './PurchaseProductStep/PurchaseProductStep';

interface PlanPurchaseFlowProps {
  customParameters?: CustomParameters;
  actions: Actions;
  onResult?: (result: PaywallResult) => void;
}

export const PlanPurchaseFlow = ({
  actions,
  customParameters,
  onResult = noop,
}: PlanPurchaseFlowProps) => {
  const {
    store,
    currentStep,
    profileResponse,
    handlePurchaseCancel,
    handleChangeCancel,
    handleCloseAndGoHome,
    handleProductPicked,
    handlePaymentMethodAdded,
    purchasableProducts,
    packageDefinition,
    purchasableProductsError,
    isPurchasableProductsLoading,
    isPaymentMethodLoading,
    otherPlatformError,
  } = usePlanPurchaseFlow({
    actions,
  });

  // Following values are required before we can start the actual flow.
  const canStartFlow =
    !isPaymentMethodLoading &&
    !isPurchasableProductsLoading &&
    !!purchasableProducts &&
    !!packageDefinition;

  if (!canStartFlow) {
    return <StyledSpinner />;
  }

  // A user cannot purchase additonal products within the same subscription group if they already have bought a product on another platform orignally.
  // Therefore, we show an error message which redirects the user to that specific platform to manage their subscription.
  if (otherPlatformError) {
    return (
      <Status
        applyDefaultGradient={false}
        error={otherPlatformError}
        onDismiss={() => actions.handleClose()}
      />
    );
  }

  if (purchasableProductsError) {
    return (
      <Status
        applyDefaultGradient={false}
        error={new OneError(Errors.PURCHASE_VALIDATION_UNKNOWN)}
        onDismiss={handleCloseAndGoHome}
      />
    );
  }

  if (profileResponse?._meta.errors?.entitlements) {
    return (
      <Status
        error={new OneError(Errors.ENTITLEMENT_PROFILE_ERROR)}
        onDismiss={() => {
          authActions.refreshProfile();
          actions.handleClose();
        }}
      />
    );
  }

  switch (currentStep) {
    case PurchaseFlowStep.PICK:
      return (
        <PickProductStep
          actions={actions}
          currentStep={currentStep}
          customParameters={customParameters}
          onProductPicked={handleProductPicked}
          packageDefinition={packageDefinition}
          purchasableProducts={purchasableProducts}
        />
      );
    case PurchaseFlowStep.PURCHASE:
      return (
        <PurchaseProductStep
          actions={actions}
          currentStep={currentStep}
          customParameters={customParameters}
          onProductChange={handlePurchaseCancel}
          onPurchaseComplete={onResult}
          selectedProduct={store.selectedProduct}
        />
      );
    case PurchaseFlowStep.CHANGE_ADD_PAYMENT_METHOD:
      return (
        <ChangeProductAddPaymentMethod
          actions={actions}
          onPaymentMethodAdded={handlePaymentMethodAdded}
          store={store}
        />
      );
    case PurchaseFlowStep.CHANGE_CONFIRM:
      return (
        <ChangeProductConfirmation
          actions={actions}
          onChangeCancel={handleChangeCancel}
          onErrorCancel={handleCloseAndGoHome}
          purchasableProducts={purchasableProducts}
          store={store}
        />
      );
  }
};
